import { memo, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { ListingGalleryPreviewProps } from "./types";
import { PictureGalleryPreview } from "ui/components/Pictures";

const ListingGalleryPreview: React.FC<ListingGalleryPreviewProps> = (props) => {
  const { allPictures, channel, marketplaceChannel } = props;
  const { t } = useTranslate();

  const isMarketplace = channel.salesChannelCode === "marketplace";

  const channelImageIds = useMemo(
    () => channel.images.map((img) => img.uploadId),
    [channel.images]
  );

  const marketplaceImageIds = useMemo(
    () => marketplaceChannel?.images.map((img) => img.uploadId) ?? [],
    [marketplaceChannel?.images]
  );

  const hasOverride = !isMarketplace && channelImageIds.length > 0;
  const displayIds = isMarketplace ? channelImageIds : hasOverride ? channelImageIds : marketplaceImageIds;

  const dummyImages = [
    { id: "dummy-1", url: "/media/images/logo.png", name: "" },
    { id: "dummy-2", url: "/media/images/logo.png", name: "" },
    { id: "dummy-3", url: "/media/images/logo.png", name: "" },
    { id: "dummy-4", url: "/media/images/logo.png", name: "" },
    { id: "dummy-5", url: "/media/images/logo.png", name: "" },
    { id: "dummy-6", url: "/media/images/logo.png", name: "" },
  ];

  // Prikazuju se isključivo slike odabrane na oglasu. Prije se, kad kanal nema
  // nijednu link-anu sliku, padalo na sve slike vozila — pa su se prikazivale i
  // one koje je korisnik namjerno odznačio.
  const selectedPictures = allPictures.filter((p) => displayIds.includes(p.id));
  const isDummy = selectedPictures.length === 0;
  const pictures = isDummy ? dummyImages : selectedPictures.map((p) => ({ id: p.id, url: p.url, name: p.name }));
  const visiblePictures = pictures.slice(0, 5);
  const remainingCount = pictures.length - 5;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedPictureId, setSelectedPictureId] = useState<string | null>(null);

  const handlePictureClick = (pictureId: string) => {
    if (isDummy) return;
    setSelectedPictureId(pictureId);
    setPreviewOpen(true);
  };

  const getGridTemplateColumns = () => {
    if (visiblePictures.length === 1) return "1fr";
    if (visiblePictures.length === 2) return "1fr 1fr";
    if (visiblePictures.length === 3) return "1fr 1fr 1fr";
    if (visiblePictures.length === 4) return "2fr 2fr 1fr";
    return "2fr 2fr 1fr 2fr";
  };

  const getGridTemplateRows = () => {
    return visiblePictures.length <= 3 ? "280px" : "140px 140px";
  };

  const getRadiusClasses = (index: number, total: number) => {
    if (total === 1) return "rounded-lg";
    if (total === 2) {
      if (index === 0) return "rounded-l-lg";
      if (index === 1) return "rounded-r-lg";
    }
    if (total === 3) {
      if (index === 0) return "rounded-l-lg";
      if (index === 2) return "rounded-r-lg";
    }
    if (total === 4) {
      if (index === 0) return "rounded-l-lg";
      if (index === 2) return "rounded-tr-lg";
      if (index === 3) return "rounded-br-lg";
    }
    if (total >= 5) {
      if (index === 0) return "rounded-l-lg";
      if (index === 4) return "rounded-r-lg";
    }
    return "";
  };

  const getGridPosition = (index: number, total: number): React.CSSProperties => {
    if (total >= 5) {
      switch (index) {
        case 0: return { gridColumn: "1", gridRow: "1 / 3" };
        case 1: return { gridColumn: "2", gridRow: "1 / 3" };
        case 2: return { gridColumn: "3", gridRow: "1" };
        case 3: return { gridColumn: "3", gridRow: "2" };
        case 4: return { gridColumn: "4", gridRow: "1 / 3" };
      }
    }
    if (total === 4) {
      switch (index) {
        case 0: return { gridColumn: "1", gridRow: "1 / 3" };
        case 1: return { gridColumn: "2", gridRow: "1 / 3" };
        case 2: return { gridColumn: "3", gridRow: "1" };
        case 3: return { gridColumn: "3", gridRow: "2" };
      }
    }
    return {};
  };

  return (
    <>
      <div
        className="pt-4 px-4 grid gap-2 rounded-lg overflow-hidden mb-3"
        style={{
          gridTemplateColumns: getGridTemplateColumns(),
          gridTemplateRows: getGridTemplateRows(),
        }}
      >
        {visiblePictures.map((picture, index) => {
          const total = visiblePictures.length;
          const isLastVisible = index === total - 1;
          const showOverlay = isLastVisible && remainingCount > 0;

          return (
            <div
              key={picture.id}
              className={`relative overflow-hidden bg-gray-100 ${!isDummy ? "cursor-pointer" : ""} ${getRadiusClasses(index, total)}`}
              style={getGridPosition(index, total)}
              onClick={() => handlePictureClick(picture.id)}
            >
              <img
                src={picture.url}
                alt={picture.name || `Slika ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {showOverlay && !isDummy && (
                <div className="absolute inset-0 flex items-end justify-end p-3">
                  <span className="text-white text-sm font-medium bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-md">
                    {t("admin.fleet.vehicle.detail.gallery.showallphotos")} ({pictures.length})
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isMarketplace && (
        <div className="px-4 mb-3">
          <span className="text-xs text-gray-400">
            {hasOverride
              ? t("admin.sales.listing.images.hasoverride")
              : t("admin.sales.listing.images.usingmarketplace")}
          </span>
        </div>
      )}

      <PictureGalleryPreview
        show={previewOpen}
        pictures={pictures}
        initialSelectedId={selectedPictureId}
        onClose={() => { setPreviewOpen(false); setSelectedPictureId(null); }}
      />
    </>
  );
};

export default memo(ListingGalleryPreview);
